import React, { useState } from 'react'
import { useTheme } from 'particles/themes/styled'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineDot from '@mui/lab/TimelineDot'
import { TimelineContainer, StyledTimeline } from './styles'
import Text from 'particles/Text'
import Popper from 'particles/Popper'
import PopperContent, { PopperContentProps } from './PopperContent'
import { COUNTRY_ENUM } from 'services/api/constants'

interface TimelineProps extends PopperContentProps {
    country: COUNTRY_ENUM
    title: string
    progress: number
    dotNumber: 2 | 3
    styleType?: 'short' | 'default'
    titleOnTopInFirstBreakPoint?: boolean
    isMobileSize?: boolean
    disableHoverMenu?: boolean
}

// we use number array to determine line progress style.
// for dotNumber === 2 case, the second index of array will always be 0 since there don't have 2nd line there
const getProgressStyleProgress = (progress: number, dotNumber: 2 | 3): number[] => {
    if (dotNumber === 2) {
        if (progress < 0) {
            return [0, 0]
        } else if (progress > 100) {
            return [100, 0]
        } else {
            return [progress, 0]
        }
    } else {
        if (progress <= 50 && progress >= 0) {
            return [progress * 2, 0]
        } else if (progress > 50 && progress <= 100) {
            return [100, (progress - 50) * 2]
        } else {
            if (progress > 100) {
                return [100, 100]
            } else {
                return [0, 0]
            }
        }
    }
}

const getTimelineComponent = (
    progress: number,
    dotNumber: 2 | 3,
    styleType: 'short' | 'default',
    handleHover: (e: React.MouseEvent<HTMLElement>) => void,
    handleHoverOver: (e: React.MouseEvent<HTMLElement>) => void,
) => {
    const theme = useTheme()
    const styledProgressArr = getProgressStyleProgress(progress, dotNumber)
    return (
        <StyledTimeline
            styledProgress={styledProgressArr}
            styleType={styleType}
            dotNumber={dotNumber}
            onMouseOverCapture={handleHover}
            onMouseOut={handleHoverOver}
        >
            <TimelineItem>
                <TimelineSeparator>
                    <TimelineDot sx={{ backgroundColor: theme.colors.surface.positive }} />
                    <TimelineConnector className="first-line" />
                </TimelineSeparator>
            </TimelineItem>
            {dotNumber === 3 && (
                <TimelineItem>
                    <TimelineSeparator>
                        <TimelineDot sx={{ backgroundColor: theme.colors.surface.warning }} />
                        <TimelineConnector className="second-line" />
                    </TimelineSeparator>
                </TimelineItem>
            )}
            <TimelineItem>
                <TimelineSeparator>
                    <TimelineDot sx={{ backgroundColor: theme.colors.surface.error }} />
                </TimelineSeparator>
            </TimelineItem>
        </StyledTimeline>
    )
}

const ProgressTimeline: React.FC<TimelineProps> = ({
    country,
    title,
    progress,
    dotNumber,
    styleType = 'default',
    titleOnTopInFirstBreakPoint = false,
    isMobileSize = false,
    disableHoverMenu = false,
    ...props
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const handleHover = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        setAnchorEl(e.currentTarget.parentElement)
    }

    const handleHoverOver = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        setAnchorEl(null)
    }

    return (
        <TimelineContainer titleOnTopInFirstBreakPoint={titleOnTopInFirstBreakPoint} styleType={styleType}>
            <div className="first-row">
                <div className="title-container">
                    <Text className="title" color="secondary" scale={isMobileSize ? 'bodyS500' : 'bodyM500'}>
                        {title}
                    </Text>
                </div>
                <div className="area">
                    {getTimelineComponent(progress, dotNumber, styleType, handleHover, handleHoverOver)}
                </div>
                {!disableHoverMenu && (
                    <Popper
                        anchorEl={anchorEl}
                        placement={'bottom-start'}
                        onClose={() => {
                            setAnchorEl(null)
                        }}
                        style={{ zIndex: 1 }}
                    >
                        <PopperContent country={country} {...props} />
                    </Popper>
                )}
            </div>
        </TimelineContainer>
    )
}

export default ProgressTimeline
